// Dynamic medication detail page — driven entirely by /api/catalog/medications/:slug.
// URL: /best/:slug   e.g. /best/tirzepatide, /best/semaglutide
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, ChevronRight, ShieldCheck, Syringe, Phone, Sparkles } from "lucide-react";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import { useLeadModal } from "@/components/shared/LeadProvider";
import { FAQAccordion } from "@/components/shared/BestRxSections";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const RED = "#E11D2A";

export default function BestMedicationPage() {
  const { slug, city } = useParams();
  const navigate = useNavigate();
  const { open } = useLeadModal();
  const [med, setMed] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    // City-targeted page uses the merged-with-override endpoint; non-city pages
    // hit the plain medication endpoint. Both return the same shape PLUS the
    // city page adds `city`, `hero_headline`, `hero_subhead`, `local_intro`,
    // and `local_business` so the JSON-LD + hero copy can localize without
    // breaking the rest of the rendering pipeline.
    const url = city
      ? `${API}/catalog/city/${city}/${slug}`
      : `${API}/catalog/medications/${slug}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((m) => {
        setMed(m);
        return fetch(`${API}/catalog/medications?category=${m.category}`)
          .then((r) => r.json())
          .then((all) => setRelated(all.filter((x) => x.slug !== m.slug).slice(0, 3)));
      })
      .catch((e) => setErr(e));
  }, [slug, city]);

  const start = (dose) => open({
    source: `best_${slug}${dose ? "_" + dose : ""}`,
    medSlug: med?.slug,
    medName: med?.name,
    medStrength: dose || null,
    assessmentImage: med?.assessment_image || med?.product_image || med?.image_url,
  });

  const heroImg = med?.hero_image || med?.product_image || med?.image_url;
  const productImg = med?.product_image || med?.image_url || med?.hero_image;
  const ogImg = med?.social_image || med?.og_image || med?.image_url;

  const schema = useMemo(() => {
    if (!med) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://bestrx.online";
    const isCity = !!med.city;
    const canonPath = isCity ? `/best/${med.city.slug}/${med.slug}` : `/best/${med.slug}`;
    const canonical = `${origin}${canonPath}`;
    const out = [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": isCity ? `${med.name} in ${med.city.name}, ${med.city.state}` : med.name,
        "description": med.short_description || med.description,
        "image": origin + (ogImg || ""),
        "brand": { "@type": "Brand", "name": "BestRx" },
        "offers": {
          "@type": "Offer",
          "priceCurrency": "USD",
          "price": (med.starting_price_cents / 100).toFixed(2),
          "availability": med.active ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "url": canonical,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": origin + "/" },
          { "@type": "ListItem", "position": 2, "name": "Medications", "item": origin + "/#medication-zone" },
          ...(isCity
            ? [
                { "@type": "ListItem", "position": 3, "name": med.city.name, "item": `${origin}/best/${med.city.slug}/${med.slug}` },
                { "@type": "ListItem", "position": 4, "name": med.name, "item": canonical },
              ]
            : [
                { "@type": "ListItem", "position": 3, "name": med.name, "item": canonical },
              ]),
        ],
      },
    ];
    if (med.faq?.length > 0) {
      out.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": med.faq.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      });
    }
    if (isCity && med.local_business) {
      out.push({
        "@context": "https://schema.org",
        "@type": "MedicalBusiness",
        "name": med.local_business.name,
        "url": med.local_business.url,
        "telephone": med.local_business.telephone,
        "areaServed": med.local_business.areaServed,
        "medicalSpecialty": "Weight Loss · Men's Health · Women's Health",
      });
    }
    return out;
  }, [med, ogImg]);

  if (err === 404) return <NotFound slug={slug} onBack={() => navigate("/")} />;
  if (!med) return <main className="min-h-screen bg-white" />;

  const priceFmt = `$${(med.starting_price_cents / 100).toFixed(0)}`;
  const categoryLabel = ({ "weight-loss": "Weight Loss", "mens-health": "Men's Health", "womens-health": "Women's Health" })[med.category] || med.category;

  return (
    <main className="min-h-screen bg-white text-black" data-testid="best-medication-page">
      <Helmet>
        <title>{med.city
          ? `${med.name} in ${med.city.name}, ${med.city.state} — BestRx`
          : (med.seo_title || `${med.name} — BestRx`)}</title>
        <meta name="description" content={med.city
          ? `Compounded ${med.name} for ${med.city.name}, ${med.city.state} patients. Provider-reviewed, pharmacy-shipped, no insurance required. ${med.short_description || ""}`
          : (med.seo_description || med.short_description)} />
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large" />
        <link rel="canonical" href={med.city
          ? `https://bestrx.online/best/${med.city.slug}/${med.slug}`
          : `https://bestrx.online/best/${med.slug}`} />
        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="BestRx" />
        <meta property="og:title" content={med.city
          ? `${med.name} in ${med.city.name}, ${med.city.state}`
          : (med.seo_title || med.name)} />
        <meta property="og:description" content={med.seo_description || med.short_description} />
        <meta property="og:url" content={med.city
          ? `https://bestrx.online/best/${med.city.slug}/${med.slug}`
          : `https://bestrx.online/best/${med.slug}`} />
        {ogImg && <meta property="og:image" content={ogImg.startsWith("http") ? ogImg : `https://bestrx.online${ogImg}`} />}
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={med.city
          ? `${med.name} in ${med.city.name}, ${med.city.state}`
          : (med.seo_title || med.name)} />
        <meta name="twitter:description" content={med.seo_description || med.short_description} />
        {ogImg && <meta name="twitter:image" content={ogImg.startsWith("http") ? ogImg : `https://bestrx.online${ogImg}`} />}
        {schema && schema.map((s, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>

      <Nav />

      {/* Breadcrumbs */}
      <nav className="border-b border-black/5 bg-white" aria-label="Breadcrumb">
        <ol className="max-w-[1240px] mx-auto px-5 py-3 flex flex-wrap items-center gap-2 text-[11px] tracking-[0.12em] font-extrabold uppercase text-black/55">
          <li><Link to="/" className="hover:text-black">Home</Link></li>
          <li>›</li>
          <li><Link to="/#medication-zone" className="hover:text-black">Medication Zone</Link></li>
          <li>›</li>
          <li><Link to={`/category/${med.category}`} className="hover:text-black">{categoryLabel}</Link></li>
          {med.city && (
            <>
              <li>›</li>
              <li className="hover:text-black" data-testid="breadcrumb-city">{med.city.name}, {med.city.state}</li>
            </>
          )}
          <li>›</li>
          <li className="text-black" data-testid="breadcrumb-current">{med.name}</li>
        </ol>
      </nav>

      {/* SECTION 1 — HERO */}
      <section className="relative overflow-hidden py-16 md:py-20 bg-white" data-testid="best-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-[0.12]"
            style={{ background: `radial-gradient(closest-side, ${med.cap_color}, transparent 70%)` }} />
        </div>
        <div className="relative max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-[10.5px] font-extrabold tracking-[0.22em] uppercase"
              style={{ borderColor: RED, color: RED, background: "rgba(255,42,60,0.04)" }}>
              <Sparkles size={11} /> {med.city ? `${med.city.name}, ${med.city.state}` : categoryLabel}
            </span>
            <h1 className="mt-4 font-heavy tracking-[-0.02em] leading-[0.92] text-black text-5xl sm:text-6xl lg:text-7xl"
              data-testid="hero-headline">
              {med.city
                ? <>{med.name.toUpperCase()}<br /><span style={{ color: RED }}>IN {med.city.name.toUpperCase()}.</span></>
                : med.name.toUpperCase()}
            </h1>
            <p className="mt-5 text-[17px] sm:text-[19px] text-black/70 leading-relaxed max-w-[640px]" data-testid="hero-short">
              {med.city ? med.hero_subhead : med.short_description}
            </p>
            <div className="mt-6 flex items-end gap-6">
              <div>
                <p className="text-[10px] tracking-[0.20em] font-extrabold uppercase text-black/45">Starting At</p>
                <p className="font-heavy leading-none" style={{ color: RED, fontSize: "44px" }}>
                  {priceFmt}<span className="text-black/45 text-[13px] font-extrabold ml-1">/mo</span>
                </p>
              </div>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => start()} data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-[13px] font-extrabold tracking-[0.16em] uppercase text-white"
                style={{ background: RED, boxShadow: "0 16px 36px -10px rgba(255,42,60,0.50)" }}>
                Get Started <ArrowUpRight size={14} strokeWidth={2.8} />
              </button>
              <a href="#what-is-it" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-[12px] font-extrabold tracking-[0.14em] uppercase border"
                style={{ borderColor: "rgba(0,0,0,0.15)" }}>
                Learn More
              </a>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.14em] font-bold text-black/60">
              <ShieldCheck size={12} color={RED} /> Provider-Reviewed · US-Licensed Pharmacies · No Insurance Required
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-square rounded-2xl overflow-hidden border bg-white"
              style={{ borderColor: "rgba(0,0,0,0.08)", boxShadow: "0 30px 80px -40px rgba(0,0,0,0.30)" }}>
              {heroImg && (
                <img src={heroImg} alt={`${med.name} — hero`}
                  className="absolute inset-0 m-auto h-full w-full object-contain"
                  data-testid="hero-image" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHAT IS IT */}
      <section id="what-is-it" className="bg-white border-y border-black/5 py-16 md:py-20" data-testid="what-is-it">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Overview</p>
            <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-4xl sm:text-5xl lg:text-6xl">
              What Is<br /><span style={{ color: RED }}>{med.name}?</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            {med.city && med.local_intro && (
              <p className="text-[14.5px] md:text-[15.5px] text-black/55 italic leading-relaxed mb-5 pl-4 border-l-2"
                style={{ borderColor: RED }}
                data-testid="local-intro">
                {med.local_intro}
              </p>
            )}
            <p className="text-[16px] md:text-[17px] text-black/75 leading-relaxed">{med.description}</p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — BENEFITS */}
      {med.benefit_cards?.length > 0 && (
        <section className="bg-white py-16 md:py-20" data-testid="benefits">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10">
            <header className="max-w-[760px] mb-10">
              <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Benefits</p>
              <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-4xl sm:text-5xl lg:text-6xl">
                Why Patients Choose<br /><span style={{ color: RED }}>{med.name}.</span>
              </h2>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
              {med.benefit_cards.map((b, i) => (
                <article key={i} className="group rounded-2xl bg-white p-6 transition-all duration-500 hover:-translate-y-1"
                  data-testid={`benefit-${i}`}
                  style={{ border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 20px 60px -30px rgba(0,0,0,0.20)" }}>
                  <span className="font-heavy text-[14px] tracking-[0.18em]" style={{ color: RED }}>{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heavy text-black text-[19px] leading-snug">{b.title}</h3>
                  <p className="mt-2 text-[13px] text-black/65 leading-relaxed">{b.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 4 — AVAILABLE STRENGTHS */}
      {med.strengths?.length > 0 && (
        <section className="bg-white border-t border-black/5 py-16 md:py-20" id="strengths" data-testid="strengths">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10">
            <header className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Available Strengths</p>
                <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-4xl sm:text-5xl lg:text-6xl">
                  Pick A Dose,<br /><span style={{ color: RED }}>Start Today.</span>
                </h2>
              </div>
              <p className="text-[13px] text-black/55 max-w-[280px]">
                Tap any strength to begin your guided assessment. A licensed provider sets the final dose.
              </p>
            </header>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
              {med.strengths.map((s) => (
                <button key={s.value} type="button"
                  onClick={() => start(`${s.value}${s.unit}`)}
                  data-testid={`strength-${s.value.replace(/\./g, "-")}`}
                  className="group relative aspect-square rounded-2xl overflow-hidden bg-white p-4 text-left transition-all duration-300 hover:-translate-y-1"
                  style={{
                    border: "1.5px solid rgba(0,0,0,0.10)",
                    boxShadow: "0 16px 50px -30px rgba(0,0,0,0.18)",
                  }}>
                  {s.is_popular && (
                    <span className="absolute top-3 right-3 text-[9px] tracking-[0.18em] font-extrabold uppercase px-1.5 py-0.5 rounded-full text-white"
                      style={{ background: RED }}>Popular</span>
                  )}
                  <Syringe size={16} className="text-black/35" />
                  <div className="absolute inset-x-4 bottom-4">
                    <p className="font-heavy leading-none tracking-tight text-black" style={{ fontSize: "clamp(28px, 3.6vw, 42px)" }}>
                      {s.value}<span className="text-black/40 font-extrabold text-[14px] tracking-tight ml-1">{s.unit}</span>
                    </p>
                    <p className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-extrabold tracking-[0.14em] uppercase text-black/55 group-hover:text-[#E11D2A]">
                      Begin Assessment <ChevronRight size={11} />
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 5 — FAQ */}
      {med.faq?.length > 0 && (
        <FAQAccordion items={med.faq} title={`${med.name} — Frequently Asked Questions`} />
      )}

      {/* SECTION 5.5 — GALLERY (per-medication, optional) */}
      {med.gallery_images?.length > 0 && (
        <section className="bg-white border-t border-black/5 py-16" data-testid="gallery">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10">
            <header className="mb-8">
              <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Gallery</p>
              <h2 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-3xl sm:text-4xl">
                Inside The <span style={{ color: RED }}>{med.name}</span> Program.
              </h2>
            </header>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
              {med.gallery_images.map((src, i) => (
                <div key={i} data-testid={`gallery-${i}`}
                  className="relative aspect-square rounded-xl overflow-hidden border bg-white"
                  style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                  <img src={src} alt={`${med.name} — ${i + 1}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 6 — RELATED MEDICATIONS (auto: same category) */}
      {related.length > 0 && (
        <section className="bg-white border-t border-black/5 py-16" data-testid="related-meds">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10">
            <h2 className="font-heavy tracking-[-0.02em] text-black text-3xl sm:text-4xl">Related Medications</h2>
            <p className="text-[13px] text-black/55 mt-2 mb-8">Other programs in {categoryLabel.toLowerCase()}.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/best/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group rounded-2xl border bg-white p-5 transition-all duration-500 hover:-translate-y-1"
                  style={{ borderColor: "rgba(0,0,0,0.08)", boxShadow: "0 20px 60px -30px rgba(0,0,0,0.20)" }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heavy text-[14px]" style={{ color: r.cap_color }}>{r.category.toUpperCase()}</span>
                    <ArrowUpRight size={14} className="text-black/45" />
                  </div>
                  {(r.card_image || r.image_url) && (
                    <img src={r.card_image || r.image_url} alt={r.name} className="h-32 w-full object-contain" />
                  )}
                  <h3 className="mt-3 font-heavy text-black text-[18px]">{r.name}</h3>
                  <p className="text-[12px] text-black/55 mt-1 line-clamp-2">{r.short_description}</p>
                  <p className="mt-3 font-heavy text-[18px]" style={{ color: RED }}>
                    ${(r.starting_price_cents / 100).toFixed(0)}<span className="text-black/45 text-[11px] font-extrabold ml-0.5">/mo</span>
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 6b — PATIENTS ALSO EXPLORE (admin-curated cross-sell rail) */}
      <CrossSellRail slug={med.slug} categoryLabel={categoryLabel} />

      {/* SECTION 7 — ASSESSMENT TRIGGER (sticky bottom CTA) */}
      <section className="relative overflow-hidden py-16 md:py-20"
        style={{ background: "linear-gradient(180deg, #0A0B10 0%, #14151C 100%)" }}
        data-testid="bottom-cta">
        <div className="relative max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 text-white">
            <p className="text-[11px] tracking-[0.24em] font-extrabold uppercase" style={{ color: RED }}>Ready To Get Started?</p>
            <h3 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-white text-4xl sm:text-5xl lg:text-6xl">
              We Call You In<br /><span style={{ color: RED }}>30 Minutes.</span>
            </h3>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
            <button onClick={() => start()} data-testid="bottom-cta-start"
              className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 text-[13.5px] font-extrabold tracking-[0.16em] uppercase text-white"
              style={{ background: RED, boxShadow: "0 16px 36px -10px rgba(255,42,60,0.60)" }}>
              Start Now <ArrowUpRight size={15} strokeWidth={2.8} />
            </button>
            <a href="tel:+12148155966"
              className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[12px] font-extrabold tracking-[0.14em] uppercase border border-white/30 text-white hover:bg-white/10">
              <Phone size={13} /> (214) 815-5966
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function NotFound({ slug, onBack }) {
  return (
    <main className="min-h-screen bg-white text-black flex items-center justify-center px-5">
      <div className="text-center" data-testid="best-notfound">
        <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>404</p>
        <h1 className="mt-3 font-heavy text-5xl">Medication not found</h1>
        <p className="mt-3 text-black/55">We couldn't find <code className="font-mono">{slug}</code> in the catalog.</p>
        <button onClick={onBack} className="mt-6 px-5 py-2.5 text-[11px] font-extrabold tracking-[0.14em] uppercase text-white"
          style={{ background: RED }}>Back Home</button>
      </div>
    </main>
  );
}


/* "Patients Also Explore" — admin-curated cross-sell rail.
   Calls /api/recommendations/cross-sell/:slug which returns either the
   medication's curated cross_sell_slugs (when an admin set them) or an
   auto-derived list of other meds in the same category. */
function CrossSellRail({ slug, categoryLabel }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let live = true;
    fetch(`${API}/recommendations/cross-sell/${slug}?limit=3`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live) setItems(d?.items || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [slug]);
  if (items.length === 0) return null;
  return (
    <section className="bg-[#FAFAFC] border-t border-black/5 py-16" data-testid="cross-sell-rail">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-10">
        <header className="mb-8">
          <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Patients Also Explore</p>
          <h2 className="mt-3 font-heavy tracking-[-0.02em] text-black text-3xl sm:text-4xl">
            What Else <span style={{ color: RED }}>Pairs Well.</span>
          </h2>
          <p className="text-[13px] text-black/55 mt-2 max-w-[560px]">
            Curated by our care team — medications patients commonly explore alongside this one.
          </p>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {items.map((r) => (
            <Link key={r.slug} to={`/best/${r.slug}`}
              data-testid={`cross-sell-${r.slug}`}
              className="group rounded-2xl border bg-white p-5 transition-all duration-500 hover:-translate-y-1"
              style={{ borderColor: "rgba(0,0,0,0.08)", boxShadow: "0 20px 60px -30px rgba(0,0,0,0.20)" }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] tracking-[0.18em] font-extrabold uppercase text-black/45">
                  Suggested
                </span>
                <ArrowUpRight size={14} className="text-black/45" />
              </div>
              {(r.card_image || r.image_url) && (
                <img src={r.card_image || r.image_url} alt={r.name} className="h-32 w-full object-contain" />
              )}
              <h3 className="mt-3 font-heavy text-black text-[18px]">{r.name}</h3>
              <p className="text-[12px] text-black/55 mt-1 line-clamp-2">{r.short_description}</p>
              <p className="mt-3 font-heavy text-[18px]" style={{ color: RED }}>
                ${Math.round((r.starting_price_cents || 0) / 100)}
                <span className="text-black/45 text-[11px] font-extrabold ml-0.5">/mo</span>
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
